import React, { useEffect, useMemo, useRef, useState } from 'react';

import useResizeObserver from '@react-hook/resize-observer';
import analytics from '@repo/analytics';
import { CreditLimitBannerDisplayIn } from '@repo/editor/src/components/CreditsLimitBanner/CreditsLimitBanner';
import DecisionModal from '@repo/editor/src/components/DecisionModal/DecisionModal';
import { AI_GENERATE_PRODUCT_BACKGROUND } from '@repo/editor/src/global/analytics/events';
import {
  authenticationStoreSelector,
  useAuthenticationStore,
} from '@repo/editor/src/stores/authenticationStore';
import {
  promotionSelector,
  usePromotionStore,
} from '@repo/editor/src/stores/promotionStore';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import {
  getUserCreditsInfo,
  userHasEnoughCredits,
} from '@repo/editor/src/utils/credits';
import { withEventScope } from '@repo/event-processor';
import NotificationCrossIcon from '@repo/ui/components/Icon/NotificationCrossIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { themeStyle } from '@repo/ui/theming';

import GallerySection from 'components/BackgroundReplacer/BackgroundReplacerTool/GallerySection';
import Scene from 'components/BackgroundReplacer/Scene/Scene';
import { PresetGroup } from 'components/BackgroundReplacer/Scene/types';
import SourceTemplate from 'components/BackgroundReplacer/SourceTemplate/SourceTemplate';
import { BackgroundGenerationOptions } from 'components/BackgroundReplacer/types';
import useWindowSize from 'global/utils/useWindowSize';
import { useBackgroundReplacerStore } from 'stores/backgroundReplacerStore/backgroundReplacerStore';
import {
  GenerateImageError,
  canBeInProgress,
  hasInitialImage,
  hasProductImage,
  imageCanBeEdited,
  RemoveBackgroundError,
} from 'stores/backgroundReplacerStore/types';
import { FlexColumn } from 'styles/layouts';

import FilePicker from '../../AiTools/FilePicker/FilePicker';
import UpgradeNotificationBox from '../../AiTools/UpgradeNotificatioBox/UpgradeNotificationBox';

import { SourceTemplateWrapper, Wrapper } from './styles';
import { getGeneratingButtonLabel, getItemsPerPage } from './utils';

const BackgroundReplacerTool: React.FC<{
  galleryTitle?: string;
  presets: PresetGroup[];
}> = ({ galleryTitle, presets }) => {
  const user = useUserStore(userStoreSelector.user);
  const galleryRef = useRef<HTMLDivElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const [showRequestBlocked, setShowRequestBlocked] = useState(false);

  const showUpgradeModal = usePromotionStore(
    promotionSelector.showUpgradeModal
  );
  const openAuthentication = useAuthenticationStore(
    authenticationStoreSelector.openAuthentication
  );
  const updateUserInfo = useUserStore(userStoreSelector.updateUserInfo);

  const { width } = useWindowSize();

  const userHasCredits = userHasEnoughCredits(user);
  const itemsPerPage = getItemsPerPage(width);

  const {
    state,
    initialize,
    reset,
    updatePresetLabels,
    selectPage,
    selectImage,
    generatePredefinedImages,
    generateImage,
  } = useBackgroundReplacerStore();

  const presetLabels = useMemo(
    () =>
      Object.fromEntries(
        presets.flatMap((x) => x.presets).map((x) => [x.id, x.label])
      ),
    [presets]
  );

  useEffect(() => {
    if (user?.id) {
      void initialize(itemsPerPage, user.id, presetLabels);
    }
  }, [user, initialize, itemsPerPage, presetLabels]);

  useEffect(() => {
    void updatePresetLabels(presetLabels);
  }, [presetLabels, updatePresetLabels]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset, user?.id]);

  const handleSelectPage = async (page: number): Promise<void> => {
    await selectPage(page, itemsPerPage);
  };

  useResizeObserver(wrapper, async () => {
    if (!wrapper.current) {
      return;
    }

    await selectPage(null, itemsPerPage);
  });

  const handleOnSignUp = (): void => {
    openAuthentication({
      blurBackground: true,
    });
  };

  const forceUpgrade = (): boolean => {
    if (!user) {
      handleOnSignUp();
      return true;
    }

    if (!userHasEnoughCredits(user) || state === 'loading') {
      showUpgradeModal('backgroundReplacer');
      return true;
    }

    return false;
  };

  const handleSelectFile = async (
    file: File,
    replace: boolean
  ): Promise<void> => {
    if (forceUpgrade()) {
      return;
    }

    const result = await selectImage(file, replace);
    if (result.error) {
      if (result.error === RemoveBackgroundError.UPLOAD_SPACE_LIMIT_REACHED) {
        showUpgradeModal('freeUploadLimitReached');
      }
      return;
    }
    if (replace || !user) {
      return;
    }

    const userCreditsInfo = getUserCreditsInfo(user);
    const creditsLimit =
      userCreditsInfo.remainingAmount + userCreditsInfo.extraCredits;

    await generatePredefinedImages(creditsLimit, () => {
      galleryRef.current?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'center',
      });
    });

    const userInfo = await updateUserInfo();
    if (userInfo && !userHasEnoughCredits(userInfo)) {
      // show upgrade modal, when the user used their last credits
      showUpgradeModal('backgroundReplacer');
    }
  };

  const handleGenerateClick = async (
    options: BackgroundGenerationOptions
  ): Promise<void> => {
    if (forceUpgrade()) {
      return;
    }

    analytics.track(AI_GENERATE_PRODUCT_BACKGROUND);

    const result = await generateImage(options);
    if (result.error === GenerateImageError.UNSAFE_PROMPT) {
      setShowRequestBlocked(true);
      return;
    }

    galleryRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'center',
    });

    const userInfo = await updateUserInfo();
    if (userInfo && !userHasEnoughCredits(userInfo)) {
      // show upgrade modal, when the user used their last credits
      showUpgradeModal('backgroundReplacer');
    }
  };

  return (
    <>
      <FlexColumn ref={wrapper} width="100%">
        <UpgradeNotificationBox
          displayIn={CreditLimitBannerDisplayIn.BACKGROUND_REPLACER_WEBSITE}
          upgradeModalType="backgroundReplacer"
        />
        <Spacer height="24px" />
        <Wrapper>
          <SourceTemplateWrapper>
            {hasInitialImage(state) ? (
              <SourceTemplate
                background={
                  hasProductImage(state)
                    ? '/images/bgRemoverTransparent.png'
                    : undefined
                }
                initialImageSrc={
                  hasInitialImage(state) ? state.initialImage : undefined
                }
                isLoading={
                  (hasInitialImage(state) && !imageCanBeEdited(state)) ||
                  !canBeInProgress(state)
                }
                productImageSrc={
                  hasProductImage(state) ? state.productImage : undefined
                }
                onReplace={(f): Promise<void> => handleSelectFile(f, true)}
              />
            ) : (
              <FilePicker
                disabled={!user || !userHasCredits || state === 'loading'}
                onDisabledClick={forceUpgrade}
                onFileSelect={(f): Promise<void> => handleSelectFile(f, false)}
              />
            )}
          </SourceTemplateWrapper>

          <div style={{ minWidth: 0, flex: 1 }}>
            <Scene
              buttonLabel={getGeneratingButtonLabel(state)}
              disabled={!!user && !imageCanBeEdited(state)}
              isLoading={
                (hasInitialImage(state) && !imageCanBeEdited(state)) ||
                (canBeInProgress(state) && state.inProgress)
              }
              presets={presets}
              onGenerateClick={handleGenerateClick}
            />
          </div>
        </Wrapper>
        <GallerySection
          ref={galleryRef}
          galleryTitle={galleryTitle}
          onSelectPage={handleSelectPage}
        />
      </FlexColumn>
      <DecisionModal
        firstButton={{
          label: 'Cancel',
          type: 'primary',
          onClick: (): void => setShowRequestBlocked(false),
        }}
        icon={NotificationCrossIcon}
        iconTheme={{
          color: themeStyle.varAlertRed,
          secondaryColor: themeStyle.varBackgroundAlt,
        }}
        isOpen={showRequestBlocked}
        message="Your request may not follow our content policy. Prompts that contain pornographic, immoral, or violence-glorifying terms are blocked."
        title="Request blocked"
        onClose={(): void => setShowRequestBlocked(false)}
      />
    </>
  );
};

export default withEventScope(BackgroundReplacerTool, {
  feature: 'backgroundReplacerTool',
});
